import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BsPlus } from "react-icons/bs";
import {
  FiBookOpen,
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiFilm,
  FiLayers,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";
import GenericModal from "../components/GenericModal";
import Loader from "../../components/loader/Loader";
import HelpManualVideoPlayer from "./HelpManualVideoPlayer";
import {
  deleteHelpManualSectionApi,
  deleteHelpManualTopicApi,
  getHelpManualSectionDeletePreview,
  getHelpManualTopicById,
  getSectionAndTopicMaster,
  mapHelpManualSearchResults,
  mapSectionAndTopicMaster,
  searchHelpManual,
  changeHelpManualTopicSection,
} from "./helpManualApi";

const SEARCH_IDLE_MS = 2000;

const highlightText = (text, query) => {
  if (!query?.trim() || !text) return text;
  const escaped = query.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${escaped})`, "gi"));
  const needle = query.trim().toLowerCase();
  return parts.map((part, i) =>
    part.toLowerCase() === needle ? (
      <mark key={i} className="bg-yellow-200 px-0.5 rounded">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

const highlightHtml = (html, query) => {
  if (!query?.trim() || !html) return html;
  const escaped = query.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return html.replace(
    new RegExp(`(${escaped})(?![^<]*>)`, "gi"),
    '<mark class="bg-yellow-200 px-0.5 rounded">$1</mark>'
  );
};

const HelpManual = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [topicDetails, setTopicDetails] = useState(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState("");
  const searchIdleTimerRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    type: null,
    sectionId: null,
    topicId: null,
    title: "",
    description: "",
  });
  const [infoModal, setInfoModal] = useState({
    open: false,
    title: "",
    description: "",
    type: "success",
  });
  const [dragTopic, setDragTopic] = useState(null);
  const [dropSectionId, setDropSectionId] = useState(null);
  const [dropHint, setDropHint] = useState(null);
  const dragTopicRef = useRef(null);

  const showError = (message) => {
    setInfoModal({
      open: true,
      title: "Error",
      description: message || "Something went wrong. Please try again.",
      type: "error",
    });
  };

  const loadTableOfContents = useCallback(async (preferredTopicId = null) => {
    setIsLoading(true);
    try {
      const data = await getSectionAndTopicMaster();
      const mapped = mapSectionAndTopicMaster(data);
      setSections(mapped);
      setExpandedSections(
        Object.fromEntries(mapped.map((section) => [section.id, true]))
      );

      const preferredExists = mapped.some((section) =>
        section.topics.some((topic) => topic.id === preferredTopicId)
      );
      const nextTopicId = preferredExists
        ? preferredTopicId
        : mapped[0]?.topics[0]?.id ?? null;
      setSelectedTopicId(nextTopicId);
    } catch (err) {
      console.error("GetSectionAndTopicMaster error:", err);
      setSections([]);
      setSelectedTopicId(null);
      if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else {
        showError(
          err.response?.data?.message ||
            err.response?.data?.Message ||
            "Failed to load Help Manual table of contents."
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTableOfContents();
  }, [loadTableOfContents]);

  useEffect(() => {
    if (searchIdleTimerRef.current) {
      clearTimeout(searchIdleTimerRef.current);
    }

    const query = searchQuery.trim();
    if (!query) {
      setSubmittedSearchQuery("");
      searchIdleTimerRef.current = null;
      return undefined;
    }

    searchIdleTimerRef.current = setTimeout(() => {
      setSubmittedSearchQuery(query);
      searchIdleTimerRef.current = null;
    }, SEARCH_IDLE_MS);

    return () => {
      if (searchIdleTimerRef.current) {
        clearTimeout(searchIdleTimerRef.current);
        searchIdleTimerRef.current = null;
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    const q = submittedSearchQuery.trim();
    if (!q) {
      searchRequestIdRef.current += 1;
      setSearchResults(null);
      setSearchLoading(false);
      return undefined;
    }

    const requestId = ++searchRequestIdRef.current;
    setSearchLoading(true);

    const search = async () => {
      try {
        const data = await searchHelpManual(q);
        if (requestId !== searchRequestIdRef.current) return;

        const searchApplied = data.searchApplied ?? data.SearchApplied;
        if (!searchApplied) {
          setSearchResults(null);
          return;
        }

        const mapped = mapHelpManualSearchResults(data);
        setSearchResults(mapped);
        setExpandedSections(
          Object.fromEntries(mapped.map((section) => [section.id, true]))
        );

        const stillSelected = mapped.some((section) =>
          section.topics.some((topic) => topic.id === selectedTopicId)
        );
        if (!stillSelected) {
          setSelectedTopicId(mapped[0]?.topics[0]?.id ?? null);
        }
      } catch (err) {
        if (requestId !== searchRequestIdRef.current) return;
        console.error("SearchHelpManual error:", err);
        setSearchResults([]);
        if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
          showError(
            "Unable to connect to server. Please check your internet connection."
          );
        } else {
          showError(
            err.response?.data?.message ||
              err.response?.data?.Message ||
              "Failed to search Help Manual."
          );
        }
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setSearchLoading(false);
        }
      }
    };

    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedSearchQuery]);

  useEffect(() => {
    let cancelled = false;

    const loadTopicDetails = async () => {
      if (!selectedTopicId) {
        setTopicDetails(null);
        return;
      }

      setTopicLoading(true);
      try {
        const result = await getHelpManualTopicById(selectedTopicId);
        const statusCode = result.statusCode ?? result.StatusCode;
        const payload = result.data ?? result.Data;

        if (cancelled) return;

        if (statusCode === 1 && payload) {
          const videoPath = payload.videoPath ?? payload.VideoPath ?? null;
          const description =
            payload.description ?? payload.Description ?? "";
          setTopicDetails({
            id: payload.id ?? payload.Id,
            name: payload.name ?? payload.Name ?? "",
            explanation: description,
            video: videoPath
              ? {
                  name: String(videoPath).split(/[/\\]/).pop() || videoPath,
                  sizeLabel: "",
                  ready: true,
                  url: videoPath,
                }
              : null,
          });
        } else {
          setTopicDetails(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("GetHelpManualTopic error:", err);
          setTopicDetails(null);
        }
      } finally {
        if (!cancelled) {
          setTopicLoading(false);
        }
      }
    };

    loadTopicDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedTopicId]);

  const displayedSections = useMemo(() => {
    if (submittedSearchQuery.trim()) {
      return searchResults ?? [];
    }
    return sections;
  }, [submittedSearchQuery, searchResults, sections]);

  const selectedTopic = useMemo(() => {
    const pools = [displayedSections, sections];
    for (const pool of pools) {
      for (const section of pool) {
        const topic = section.topics.find((t) => t.id === selectedTopicId);
        if (topic) {
          return {
            section,
            topic: {
              ...topic,
              name: topicDetails?.name || topic.name,
              explanation:
                topicDetails?.explanation ?? topic.explanation ?? "",
              video: topicDetails?.video ?? topic.video,
            },
          };
        }
      }
    }
    return null;
  }, [displayedSections, sections, selectedTopicId, topicDetails]);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const openAddSection = () => {
    navigate("/helpmanual/sections/add");
  };

  const openEditSection = (section) => {
    navigate(`/helpmanual/sections/edit/${section.id}`);
  };

  const openAddTopic = (sectionId) => {
    navigate(`/helpmanual/topics/add/${sectionId}`);
  };

  const openEditTopic = (sectionId, topic) => {
    navigate(`/helpmanual/topics/edit/${sectionId}/${topic.id}`);
  };

  const requestDeleteSection = async (section) => {
    setIsLoading(true);
    try {
      const preview = await getHelpManualSectionDeletePreview(section.id);
      const sectionName =
        preview.sectionName ?? preview.SectionName ?? section.name;
      const message =
        preview.confirmationMessage ??
        preview.ConfirmationMessage ??
        `Deleting "${sectionName}" will also remove its topics. This cannot be undone.`;

      setDeleteModal({
        open: true,
        type: "section",
        sectionId: section.id,
        topicId: null,
        title: "Delete section?",
        description: message,
      });
    } catch (err) {
      console.error("GetHelpManualSectionDeletePreview error:", err);
      if (err.response?.status === 404) {
        showError("Section was not found or is already deleted.");
      } else if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else {
        showError(
          err.response?.data?.message ||
            err.response?.data?.Message ||
            "Failed to load delete confirmation."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const requestDeleteTopic = (section, topic) => {
    setDeleteModal({
      open: true,
      type: "topic",
      sectionId: section.id,
      topicId: topic.id,
      title: "Delete topic?",
      description: `Are you sure you want to delete "${topic.name}"?`,
    });
  };

  const confirmDelete = async () => {
    const wasSection = deleteModal.type === "section";
    const targetSectionId = deleteModal.sectionId;
    const targetTopicId = deleteModal.topicId;

    setDeleteModal({
      open: false,
      type: null,
      sectionId: null,
      topicId: null,
      title: "",
      description: "",
    });
    setIsLoading(true);

    try {
      if (wasSection) {
        const result = await deleteHelpManualSectionApi({
          id: targetSectionId,
          isConfirmed: true,
        });
        const statusCode = result.statusCode ?? result.StatusCode;
        const message = result.message ?? result.Message;
        if (statusCode !== 1) {
          showError(message || "Failed to delete section.");
          return;
        }
      } else {
        const result = await deleteHelpManualTopicApi(targetTopicId);
        const statusCode = result.statusCode ?? result.StatusCode;
        const message = result.message ?? result.Message;
        if (statusCode !== 1) {
          showError(message || "Failed to delete topic.");
          return;
        }
      }

      setSearchQuery("");
      setSubmittedSearchQuery("");
      setSearchResults(null);
      await loadTableOfContents(
        wasSection || selectedTopicId === targetTopicId
          ? null
          : selectedTopicId
      );

      setInfoModal({
        open: true,
        title: wasSection ? "Section deleted" : "Topic deleted",
        description: wasSection
          ? "The section and all of its topics have been removed."
          : "The topic has been removed.",
        type: "success",
      });
    } catch (err) {
      console.error("Delete help manual error:", err);
      showError(
        err.response?.data?.message ||
          err.response?.data?.Message ||
          "Failed to delete. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isSearching = Boolean(submittedSearchQuery.trim());

  const handleTopicDragStart = (event, section, topic) => {
    if (isSearching || event.target.closest("[data-no-drag]")) {
      event.preventDefault();
      return;
    }

    const payload = { id: topic.id, sectionId: section.id };
    dragTopicRef.current = payload;
    setDragTopic(payload);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(topic.id));
  };

  const clearDrag = () => {
    dragTopicRef.current = null;
    setDragTopic(null);
    setDropSectionId(null);
    setDropHint(null);
  };

  const topicOrderUnchanged = (topics, draggingId, placeAfterTopicId) => {
    const ids = (topics || []).map((topic) => Number(topic.id));
    const dragId = Number(draggingId);
    const without = ids.filter((id) => id !== dragId);
    let insertAt = 0;
    if (Number(placeAfterTopicId) > 0) {
      const anchorIndex = without.indexOf(Number(placeAfterTopicId));
      if (anchorIndex < 0) return false;
      insertAt = anchorIndex + 1;
    }
    const next = [...without];
    next.splice(insertAt, 0, dragId);
    return (
      next.length === ids.length && next.every((id, index) => id === ids[index])
    );
  };

  const moveTopic = async (dragging, section, placeAfterTopicId) => {
    const sameSection = Number(dragging.sectionId) === Number(section.id);
    if (
      sameSection &&
      topicOrderUnchanged(section.topics, dragging.id, placeAfterTopicId)
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const result = await changeHelpManualTopicSection({
        id: dragging.id,
        sectionId: section.id,
        placeAfterTopicId,
      });
      const statusCode = result.statusCode ?? result.StatusCode;
      if (statusCode !== 1) {
        showError(result.message || result.Message || "Failed to move topic.");
        return;
      }

      setExpandedSections((prev) => ({ ...prev, [section.id]: true }));
      await loadTableOfContents(dragging.id);
    } catch (err) {
      console.error("ChangeHelpManualTopicSection error:", err);
      showError(
        err.response?.data?.message ||
          err.response?.data?.Message ||
          "Failed to move topic."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSectionDragOver = (event, section) => {
    const dragging = dragTopicRef.current;
    if (isSearching || !dragging) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (dropSectionId !== section.id) {
      setDropSectionId(section.id);
    }
    if (dropHint) {
      setDropHint(null);
    }
  };

  const handleSectionDrop = async (event, section) => {
    event.preventDefault();
    const dragging = dragTopicRef.current;
    clearDrag();
    if (isSearching || !dragging) {
      return;
    }

    const lastTopic = [...(section.topics || [])]
      .reverse()
      .find((topic) => Number(topic.id) !== Number(dragging.id));
    const placeAfterTopicId = lastTopic ? Number(lastTopic.id) : 0;
    await moveTopic(dragging, section, placeAfterTopicId);
  };

  const handleTopicDragOver = (event, section, topic) => {
    const dragging = dragTopicRef.current;
    if (isSearching || !dragging) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (Number(dragging.id) === Number(topic.id)) {
      return;
    }

    event.dataTransfer.dropEffect = "move";
    const rect = event.currentTarget.getBoundingClientRect();
    const placement =
      event.clientY < rect.top + rect.height / 2 ? "before" : "after";
    setDropHint((current) =>
      current &&
      current.sectionId === section.id &&
      current.topicId === topic.id &&
      current.placement === placement
        ? current
        : { sectionId: section.id, topicId: topic.id, placement }
    );
    if (dropSectionId !== null) {
      setDropSectionId(null);
    }
  };

  const handleTopicDrop = async (event, section, topic) => {
    event.preventDefault();
    event.stopPropagation();
    const dragging = dragTopicRef.current;
    const placement =
      dropHint &&
      dropHint.sectionId === section.id &&
      dropHint.topicId === topic.id
        ? dropHint.placement
        : event.clientY <
            event.currentTarget.getBoundingClientRect().top +
              event.currentTarget.getBoundingClientRect().height / 2
          ? "before"
          : "after";
    clearDrag();
    if (isSearching || !dragging || Number(dragging.id) === Number(topic.id)) {
      return;
    }

    const topics = (section.topics || []).filter(
      (item) => Number(item.id) !== Number(dragging.id)
    );
    const targetIndex = topics.findIndex(
      (item) => Number(item.id) === Number(topic.id)
    );
    if (targetIndex < 0) {
      return;
    }

    const placeAfterTopicId =
      placement === "before"
        ? targetIndex === 0
          ? 0
          : Number(topics[targetIndex - 1].id)
        : Number(topics[targetIndex].id);
    await moveTopic(dragging, section, placeAfterTopicId);
  };

  return (
    <>
      {(isLoading || topicLoading || searchLoading) && <Loader />}
      <div className="bg-white p-4 rounded-lg flex flex-col h-full min-h-0 help-manual-page">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold leading-[1.3]">Help Manual</h1>
          <p className="text-base text-primary">Help Manual</p>
        </div>

        <div className="flex justify-between items-center gap-3 mb-4">
          <div className="relative">
            <FiSearch
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchQuery(value);
                if (!value.trim()) {
                  setSubmittedSearchQuery("");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (searchIdleTimerRef.current) {
                    clearTimeout(searchIdleTimerRef.current);
                    searchIdleTimerRef.current = null;
                  }
                  setSubmittedSearchQuery(searchQuery.trim());
                }
              }}
              placeholder="Search by section, topic or explanation"
              className="border pl-9 pr-3 py-2 rounded text-sm input-field w-[320px] max-w-full"
              maxLength={256}
              aria-label="Search help manual by section name, topic name, or explanation"
            />
          </div>
          <button
            type="button"
            onClick={openAddSection}
            className="btn-add px-4 shrink-0"
            title="Add"
          >
            <BsPlus size={26} color="#ffffff" /> Section
          </button>
        </div>

        <div className="flex-1 min-h-0 min-w-0 grid grid-cols-1 xl:grid-cols-[22.5rem_minmax(0,1fr)] gap-4">
          <div className="help-manual-panel lg:min-h-0">
            <div className="help-manual-panel__header">
              <div className="flex items-center gap-2 min-w-0">
                <FiBookOpen className="w-4 h-4 shrink-0" />
                <span className="text-sm font-semibold truncate">
                  Table of Contents
                </span>
              </div>
              <span className="text-xs bg-white/15 px-2.5 py-0.5 rounded-full shrink-0">
                {displayedSections.length} Sections
              </span>
            </div>

            <div className="help-manual-panel__body table-scroll p-2">
              {!isSearching && sections.length === 0 ? (
                <div className="help-manual-empty">
                  <FiBookOpen className="w-6 h-6 text-gray-400" />
                  <p>No sections yet. Click + Section to create one.</p>
                </div>
              ) : isSearching &&
                displayedSections.length === 0 &&
                !searchLoading ? (
                <div className="help-manual-empty">
                  <FiSearch className="w-6 h-6 text-gray-400" />
                  <p>No sections or topics match your search.</p>
                </div>
              ) : (
                displayedSections.map((section) => {
                  const expanded = expandedSections[section.id] !== false;
                  return (
                    <div
                      key={section.id}
                      className={`help-manual-toc-section${
                        dropSectionId === section.id
                          ? " help-manual-toc-section--drop"
                          : ""
                      }`}
                      onDragOver={(event) => handleSectionDragOver(event, section)}
                      onDragLeave={(event) => {
                        if (event.currentTarget.contains(event.relatedTarget)) {
                          return;
                        }
                        if (dropSectionId === section.id) {
                          setDropSectionId(null);
                        }
                        if (dropHint?.sectionId === section.id) {
                          setDropHint(null);
                        }
                      }}
                      onDrop={(event) => handleSectionDrop(event, section)}
                    >
                      <div className="help-manual-toc-section__row">
                        <div className="help-manual-toc-main">
                          <button
                            type="button"
                            onClick={() => toggleSection(section.id)}
                            className="help-manual-toc-icon"
                            aria-label={
                              expanded ? "Collapse section" : "Expand section"
                            }
                          >
                            {expanded ? (
                              <FiChevronDown size={16} />
                            ) : (
                              <FiChevronRight size={16} />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleSection(section.id)}
                            className="help-manual-topic-btn font-medium text-gray-800"
                          >
                            <FiLayers className="w-4 h-4 text-primary shrink-0" />
                            <span className="text-sm truncate">
                              {highlightText(
                                section.name,
                                submittedSearchQuery
                              )}
                            </span>
                          </button>
                        </div>
                        <div className="help-manual-toc-actions">
                          <button
                            type="button"
                            onClick={() => openAddTopic(section.id)}
                            className="help-manual-toc-add"
                            title="Add topic"
                          >
                            + Topic
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditSection(section)}
                            className="help-manual-toc-icon"
                            title="Edit section"
                            aria-label="Edit section"
                          >
                            <FiEdit2 size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => requestDeleteSection(section)}
                            className="help-manual-toc-icon help-manual-toc-icon--danger"
                            title="Delete section"
                            aria-label="Delete section"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {expanded &&
                        section.topics.map((topic) => {
                          const isSelected = topic.id === selectedTopicId;
                          const topicDrop =
                            dropHint?.sectionId === section.id &&
                            dropHint?.topicId === topic.id
                              ? dropHint.placement
                              : null;
                          return (
                            <div
                              key={topic.id}
                              className={`help-manual-toc-topic ${
                                isSelected
                                  ? "help-manual-toc-topic--active"
                                  : ""
                              }${
                                topicDrop === "before"
                                  ? " help-manual-toc-topic--drop-before"
                                  : ""
                              }${
                                topicDrop === "after"
                                  ? " help-manual-toc-topic--drop-after"
                                  : ""
                              }`}
                              draggable={!isSearching}
                              onDragStart={(event) =>
                                handleTopicDragStart(event, section, topic)
                              }
                              onDragEnd={clearDrag}
                              onDragOver={(event) =>
                                handleTopicDragOver(event, section, topic)
                              }
                              onDrop={(event) =>
                                handleTopicDrop(event, section, topic)
                              }
                            >
                              <button
                                type="button"
                                draggable={!isSearching}
                                onDragStart={(event) =>
                                  handleTopicDragStart(event, section, topic)
                                }
                                onDragEnd={clearDrag}
                                onClick={() => setSelectedTopicId(topic.id)}
                                className="help-manual-topic-btn"
                              >
                                <span className="text-sm truncate">
                                  {highlightText(
                                    topic.name,
                                    submittedSearchQuery
                                  )}
                                </span>
                                {topic.video && (
                                  <span className="help-manual-video-badge">
                                    <FiFilm className="w-3 h-3" />
                                    Video
                                  </span>
                                )}
                              </button>
                              <div className="help-manual-toc-actions">
                                <span
                                  className="help-manual-toc-add"
                                  aria-hidden="true"
                                />
                                <button
                                  type="button"
                                  data-no-drag
                                  onClick={() =>
                                    openEditTopic(section.id, topic)
                                  }
                                  className="help-manual-toc-icon"
                                  title="Edit topic"
                                  aria-label="Edit topic"
                                >
                                  <FiEdit2 size={16} />
                                </button>
                                <button
                                  type="button"
                                  data-no-drag
                                  onClick={() =>
                                    requestDeleteTopic(section, topic)
                                  }
                                  className="help-manual-toc-icon help-manual-toc-icon--danger"
                                  title="Delete topic"
                                  aria-label="Delete topic"
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="help-manual-panel help-manual-content-panel min-w-0 lg:min-h-0">
            {selectedTopic ? (
              <>
                <div className="px-4 py-3 border-b border-gray-200 bg-white flex flex-wrap items-center justify-between gap-2 shrink-0">
                  <span className="help-manual-badge">
                    Section: {selectedTopic.section.name}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      openEditTopic(
                        selectedTopic.section.id,
                        selectedTopic.topic
                      )
                    }
                    className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary bg-transparent"
                    title="Edit topic"
                  >
                    <FiEdit2 size={16} />
                    Edit Topic
                  </button>
                </div>

                <div className="help-manual-panel__body table-scroll p-5 bg-white">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 break-words">
                    {highlightText(
                      selectedTopic.topic.name,
                      submittedSearchQuery
                    )}
                  </h2>

                  {selectedTopic.topic.video && (
                    <HelpManualVideoPlayer
                      src={selectedTopic.topic.video.url}
                      fileName={selectedTopic.topic.video.name}
                      sizeLabel={selectedTopic.topic.video.sizeLabel}
                    />
                  )}

                  <div
                    className="help-manual-content text-gray-700"
                    dangerouslySetInnerHTML={{
                      __html: highlightHtml(
                        selectedTopic.topic.explanation,
                        submittedSearchQuery
                      ),
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="help-manual-empty flex-1">
                <FiBookOpen className="w-8 h-8 text-gray-300" />
                <p>
                  Select a topic from the table of contents to view its content.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <GenericModal
        open={deleteModal.open}
        title={deleteModal.title}
        description={deleteModal.description}
        primaryText="Delete"
        secondaryText="Cancel"
        type="warning"
        onPrimary={confirmDelete}
        onSecondary={() =>
          setDeleteModal({
            open: false,
            type: null,
            sectionId: null,
            topicId: null,
            title: "",
            description: "",
          })
        }
        onClose={() =>
          setDeleteModal({
            open: false,
            type: null,
            sectionId: null,
            topicId: null,
            title: "",
            description: "",
          })
        }
      />

      <GenericModal
        open={infoModal.open}
        title={infoModal.title}
        description={infoModal.description}
        primaryText="OK"
        type={infoModal.type}
        onClose={() =>
          setInfoModal({
            open: false,
            title: "",
            description: "",
            type: "success",
          })
        }
      />
    </>
  );
};

export default HelpManual;
